import React, { useEffect, useState } from "react";
import Layout, { PageHeader } from "@/components/Layout";
import api, { formatApiErrorDetail, lookupPostal } from "@/lib/api";
import { DETAIL_FIELDS } from "@/pages/Shipments";
import { useAuth } from "@/context/AuthContext";
import { Plus, Loader2, Trash2, Pencil, MapPin, Building2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const empty = () => ({ company: "", address: "", postal_code: "", poblacion: "", ciudad: "", schedule: "", contact_name: "", contact_phone: "", reference: "", notes: "" });

export default function Addresses() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(!isAdmin);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [busy, setBusy] = useState(false);
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");

  const load = () => {
    if (isAdmin && !clientId) { setItems([]); return; }
    setLoading(true);
    const url = isAdmin ? `/address-book?client_id=${clientId}` : "/address-book";
    api.get(url).then((r) => setItems(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => {
    if (isAdmin) api.get("/users?role=client").then((r) => setClients(r.data)).catch(() => {});
    else load();
  }, []);
  useEffect(() => { if (isAdmin) load(); }, [clientId]);

  const clientQuery = isAdmin ? `?client_id=${clientId}` : "";

  const openNew = () => { setEditing(null); setForm(empty()); setOpen(true); };
  const openEdit = (a) => { setEditing(a); setForm({ ...empty(), ...a }); setOpen(true); };
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onPostal = async (v) => {
    setF("postal_code", v);
    const res = await lookupPostal(v);
    if (res) setForm((f) => ({ ...f, postal_code: v, poblacion: f.poblacion || res.poblacion, ciudad: f.ciudad || res.ciudad }));
  };

  const save = async () => {
    if (!form.company.trim()) { toast.error("El nombre de la empresa es obligatorio"); return; }
    setBusy(true);
    try {
      const payload = DETAIL_FIELDS.reduce((a, f) => ({ ...a, [f.k]: form[f.k] || "" }), {});
      if (editing) await api.put(`/address-book/${editing.id}${clientQuery}`, payload);
      else await api.post(`/address-book${clientQuery}`, payload);
      toast.success(editing ? "Dirección actualizada" : "Dirección guardada");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta dirección guardada?")) return;
    try { await api.delete(`/address-book/${id}${clientQuery}`); toast.success("Dirección eliminada"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const inputCls = "w-full bg-background border border-input rounded-none px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-freight";

  return (
    <Layout>
      <div className="p-4 md:p-8">
        <PageHeader
          title={isAdmin ? "Direcciones de Clientes" : "Mis Direcciones"}
          subtitle={isAdmin ? "Consulta y edita las direcciones guardadas de cada cliente" : `${items.length} dirección(es) guardada(s)`}
          action={
            <button data-testid="add-address-btn" onClick={openNew} disabled={isAdmin && !clientId}
              className="bg-freight hover:bg-freight-hover text-black font-bold uppercase text-sm px-4 py-2.5 flex items-center gap-2 transition-colors disabled:opacity-50">
              <Plus className="w-4 h-4" /> Nueva dirección
            </button>
          }
        />

        {isAdmin && (
          <div className="mb-5 max-w-sm">
            <label className="text-[10px] font-bold uppercase text-muted-foreground">Cliente</label>
            <select data-testid="addr-client-select" className={inputCls} value={clientId} onChange={(e) => setClientId(e.target.value)}>
              <option value="">— Selecciona un cliente —</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}{c.company ? ` · ${c.company}` : ""}</option>)}
            </select>
          </div>
        )}

        {isAdmin && !clientId ? (
          <div className="border border-dashed border-border p-12 text-center">
            <MapPin className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">Selecciona un cliente para ver sus direcciones guardadas.</p>
          </div>
        ) : loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-freight" /></div>
        ) : items.length === 0 ? (
          <div className="border border-dashed border-border p-12 text-center">
            <MapPin className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">No hay direcciones guardadas. Se guardan automáticamente al crear recogidas, o añádelas aquí.</p>
          </div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            {items.map((a) => (
              <div key={a.id} data-testid={`address-card-${a.id}`} className="bg-card border border-border p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 font-display font-black uppercase tracking-tight text-corp">
                    <Building2 className="w-4 h-4" /> {a.company}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button data-testid={`edit-address-${a.id}`} onClick={() => openEdit(a)} className="text-corp hover:underline"><Pencil className="w-4 h-4" /></button>
                    <button data-testid={`delete-address-${a.id}`} onClick={() => remove(a.id)} className="text-red-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                <dl className="text-sm mt-3 space-y-1">
                  {[["address", "Dirección"], ["postal_code", "C.P."], ["poblacion", "Población"], ["ciudad", "Ciudad"], ["schedule", "Horario"], ["contact_name", "Contacto"], ["contact_phone", "Teléfono"], ["reference", "Referencia"], ["notes", "Observaciones"]].map(([k, l]) => a[k] ? (
                    <div key={k} className="flex gap-2"><dt className="text-muted-foreground min-w-[90px]">{l}:</dt><dd className="font-medium">{a[k]}</dd></div>
                  ) : null)}
                </dl>
              </div>
            ))}
          </div>
        )}

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="rounded-none bg-card max-w-lg">
            <DialogHeader><DialogTitle className="font-display font-black uppercase">{editing ? "Editar Dirección" : "Nueva Dirección"}</DialogTitle></DialogHeader>
            <div className="space-y-2.5 max-h-[70vh] overflow-y-auto pr-1">
              {DETAIL_FIELDS.map((f) => (
                <div key={f.k}>
                  <label className="text-[10px] font-bold uppercase text-muted-foreground">{f.l}{f.k === "company" ? " *" : ""}</label>
                  {f.k === "notes" ? (
                    <textarea data-testid={`addr-${f.k}`} className={inputCls} value={form[f.k]} onChange={(e) => setF(f.k, e.target.value)} />
                  ) : (
                    <input data-testid={`addr-${f.k}`} className={inputCls} value={form[f.k]} onChange={(e) => f.k === "postal_code" ? onPostal(e.target.value) : setF(f.k, e.target.value)} />
                  )}
                </div>
              ))}
            </div>
            <DialogFooter>
              <button data-testid="save-address-btn" onClick={save} disabled={busy} className="bg-freight hover:bg-freight-hover text-black font-bold uppercase text-sm px-4 py-2.5 flex items-center gap-2 disabled:opacity-60">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} Guardar
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
